import { useState, Fragment, useEffect, useMemo } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { Header, Segment, Grid, Modal, Divider, List } from 'semantic-ui-react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import MenuHeader from 'components/Header/SupplierHeader'
import SupplierSidebarMenu from 'components/Sidebar/SupplierSidebarMenu'
import supplierActions from '../../store/actions/suppliers'
import Swal from 'sweetalert2'
import { initSocketHandlers } from 'utils/socket/chat/handlers'
import { useLateralMenu } from 'hooks/useLateralMenu'
import { useTabNavigation, ViewRegistrar } from 'hooks/useTabNavigation'

const PrivateSupplierRouteContent = ({
	Component,
	routeProps,
	sidebarOptions,
	isAuthenticated,
	isAllowed,
	permissionsReady,
	supplier,
	logout,
	rest,
}) => {
	const { registerActiveView, openTab } = useTabNavigation()
	const {
		setExpand,
		previousExpand,
		setPreviousExpand,
		mobileOnly,
		setMobileOnly,
	} = useLateralMenu()
	const [openModal, setOpenModal] = useState(false)
	const [sidebarMenuOptions, setSidebarMenuOptions] = useState(null)
	const handleSidebarMenuOpen = (options) => setSidebarMenuOptions(options)

	const memoizedRouteProps = useMemo(
		() => ({ ...routeProps, sidebarOptions: sidebarMenuOptions || sidebarOptions }),
		[routeProps, sidebarOptions, sidebarMenuOptions]
	)

	const memoizedLayoutConfig = useMemo(
		() => ({
			Header: MenuHeader,
			Sidebar: SupplierSidebarMenu,
			sidebarWidth: 225,
			sidebarProps: {
				supplier,
				rest: rest,
				setExpand,
				previousExpand,
				setPreviousExpand,
				openModal,
				setOpenModal,
				mobileOnly,
				setMobileOnly,
				onMenuOpen: handleSidebarMenuOpen,
				onTabOpen: (route) => openTab(route, routeProps.history.push, false),
			},
			layoutProps: { logout: logout, rest: rest },
		}),
		[
			supplier,
			logout,
			rest,
			setExpand,
			previousExpand,
			setPreviousExpand,
			openModal,
			mobileOnly,
			setMobileOnly,
			handleSidebarMenuOpen,
			openTab,
			routeProps.history.push,
		]
	)

	if (!isAuthenticated) return <Redirect to='/login' />

	return (
		<>
			<ViewRegistrar
				registerActiveView={registerActiveView}
				Component={
					!permissionsReady ? EmptyComponent : isAllowed ? Component : ModuleNotFound
				}
				routeProps={memoizedRouteProps}
				layoutConfig={memoizedLayoutConfig}
			/>
			<Modal
				closeIcon
				basic
				centered={true}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				open={openModal}
				size='large'
			>
				<Modal.Content>
					<Segment inverted style={{ height: '100%' }}>
						<Grid stackable divided inverted>
							<Grid.Row columns={2}>
								<Grid.Column width={6} verticalAlign='middle' textAlign='center'>
									<Header inverted content='© Geologistic GPS 2025' />
								</Grid.Column>
								<Grid.Column width={8}>
									<Header inverted content='Contacto' />
									<Divider></Divider>
									<Header
										size='small'
										inverted
										content='¿Necesitas registrar un nuevo ticket o solicitud?'
									/>
									<List divided inverted>
										<List.Item
											header='Ingresa al Portal: '
											content='https://geologistic.com.mx/Soporte/'
											style={{ cursor: 'pointer' }}
											onClick={() => {
												window.open(
													'https://geologistic.com.mx/Soporte/',
													'_blank'
												)
											}}
										/>
										<List.Item
											header='Soporte'
											content='(686) 214 5447, (686) 385 6938,  (686) 510 9915'
										/>
										<List.Item header='Oficina' content='(686) 372 9877' />
										<List.Item
											header='Emergencias'
											content='(686) 420 3333, (686) 214 5447, (686) 385 6938, (686) 510 9915,  (686) 123 6454'
										/>
										<List.Item header='Administración' content='(686) 122 0316' />
										<List.Item
											header='Ventas'
											content='(686) 510 9915, (686) 196 4736, (686) 420 8586,  (686) 191 1571'
										/>
									</List>
								</Grid.Column>
							</Grid.Row>
						</Grid>
					</Segment>
				</Modal.Content>
			</Modal>
		</>
	)
}

const PrivateSupplierRoute = (props) => {
	const { isAuthenticated = true, component: Component, ...rest } = props
	const dispatch = useDispatch()
	const [allowedRoutes, setAllowedRoutes] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const permissionsReady = !isLoading

	useEffect(() => {
		const loadPermissions = async () => {
			try {
				await props.getSupplierModules()
				if (props.supplier?.id) {
					const permissions = await props.getWebSupplierPermissions(props.supplier.id)
					setAllowedRoutes(permissions || [])
				}
			} catch (error) {
				setAllowedRoutes([])
			} finally {
				setIsLoading(false)
			}
		}
		loadPermissions()
	}, [props.supplier?.id, props.getSupplierModules, props.getWebSupplierPermissions])

	useEffect(() => {
		if (!props.supplier) return
		dispatch(initSocketHandlers())
	}, [dispatch, props.supplier])

	return (
		<Route
			{...rest}
			render={(routeProps) => {
				const isAllowed = allowedRoutes.some((allowedRoute) =>
					routeProps.location.pathname.startsWith(allowedRoute)
				)
				return (
					<PrivateSupplierRouteContent
						Component={Component}
						routeProps={routeProps}
						isAuthenticated={isAuthenticated}
						isAllowed={isAllowed}
						permissionsReady={permissionsReady}
						supplier={props.supplier}
						logout={rest.logout}
						rest={rest}
					/>
				)
			}}
		/>
	)
}

const mapStateToProps = (state) => ({
	isAuthenticated: !!state.app.supplier,
	supplier: state.app.supplier,
})

const mapDispatchToProps = (dispatch) => ({
	getSupplierModules: () => dispatch(supplierActions.getSupplierModules()),
	getWebSupplierPermissions: (supplierId) =>
		dispatch(supplierActions.getWebSupplierPermissions(supplierId)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PrivateSupplierRoute)

const EmptyComponent = () => null

const ModuleNotFound = () => {
	const { t } = useTranslation()
	return (
		<div style={{ padding: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
			<Header as="h1" icon="warning circle" content={t("Módulo no encontrado")} />
			<p>{t("No tienes acceso a este módulo o no existe.")}</p>
		</div>
	)
}
